import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions,
  Button,
  Space,
  Tag,
  Spin,
  Alert,
  Modal,
  message,
  Table,
  Card,
  Statistic,
  Row,
  Col,
  Select,
  Input,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CaretRightOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import {
  getCampaign,
  deleteCampaign,
  launchCampaign,
  pauseCampaign,
  resumeCampaign,
  getRecipients,
  addRecipient,
  deleteRecipient,
  updateRecipient,
  getCampaignSummary,
  type MarketingCampaign,
  type CampaignRecipient,
  type CampaignSummaryResponse,
} from '@/api/marketingApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  draft: 'default',
  scheduled: 'blue',
  active: 'green',
  paused: 'orange',
  completed: 'purple',
  cancelled: 'red',
};

const TYPE_COLORS: Record<string, string> = {
  email: 'cyan',
  sms: 'geekblue',
  social: 'magenta',
};

const RECIPIENT_STATUS_COLORS: Record<string, string> = {
  pending: 'default',
  sent: 'blue',
  delivered: 'cyan',
  opened: 'green',
  clicked: 'lime',
  bounced: 'red',
  unsubscribed: 'orange',
};

export default function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [campaign, setCampaign] = useState<MarketingCampaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<CampaignRecipient[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [summary, setSummary] = useState<CampaignSummaryResponse | null>(null);
  const [addContactId, setAddContactId] = useState('');
  const [recipientStatusFilter, setRecipientStatusFilter] = useState<string | undefined>();

  const canWrite = hasPermission('campaigns', 'write');
  const canDelete = hasPermission('campaigns', 'delete');

  const loadRecipients = useCallback(
    (campaignId: string, statusFilter?: string) => {
      setRecipientsLoading(true);
      const params = statusFilter ? { status: statusFilter } : undefined;
      getRecipients(campaignId, { from: 0, size: 200, ...params })
        .then((res) => setRecipients(res.items))
        .catch(() => setRecipients([]))
        .finally(() => setRecipientsLoading(false));
    },
    []
  );

  const loadSummary = useCallback((campaignId: string) => {
    getCampaignSummary(campaignId)
      .then((res) => setSummary(res))
      .catch(() => setSummary(null));
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getCampaign(id)
      .then((c) => {
        setCampaign(c);
        loadRecipients(id);
        loadSummary(id);
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [id, t, loadRecipients, loadSummary]);

  const handleDelete = () => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('campaigns.confirm_delete'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteCampaign(id);
          message.success(t('campaigns.delete_success'));
          navigate('/marketing');
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  };

  const handleLifecycle = async (action: 'launch' | 'pause' | 'resume') => {
    if (!id) return;
    try {
      let updated: MarketingCampaign;
      if (action === 'launch') updated = await launchCampaign(id);
      else if (action === 'pause') updated = await pauseCampaign(id);
      else updated = await resumeCampaign(id);
      setCampaign(updated);
      message.success(t(`campaigns.${action}_success`));
    } catch {
      message.error(t('app.error'));
    }
  };

  const handleAddRecipient = async () => {
    if (!id || !addContactId.trim()) return;
    try {
      await addRecipient(id, { contact_id: addContactId.trim() });
      message.success(t('campaigns.recipients.add_success'));
      setAddContactId('');
      loadRecipients(id, recipientStatusFilter);
    } catch {
      message.error(t('app.error'));
    }
  };

  const handleDeleteRecipient = (recipientId: string) => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('campaigns.recipients.confirm_delete'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteRecipient(id, recipientId);
          message.success(t('campaigns.recipients.delete_success'));
          loadRecipients(id, recipientStatusFilter);
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  };

  const handleUpdateRecipientStatus = async (recipientId: string, status: string) => {
    if (!id) return;
    try {
      await updateRecipient(id, recipientId, { status });
      message.success(t('campaigns.recipients.update_success'));
      loadRecipients(id, recipientStatusFilter);
    } catch {
      message.error(t('app.error'));
    }
  };

  const recipientColumns: ColumnsType<CampaignRecipient> = useMemo(
    () => [
      {
        title: t('campaigns.recipients.contact_id'),
        dataIndex: 'contact_id',
        key: 'contact_id',
        ellipsis: true,
      },
      {
        title: t('campaigns.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status: string) => (
          <Tag color={RECIPIENT_STATUS_COLORS[status] ?? 'default'}>
            {t(`campaigns.recipient_status.${status}`, status)}
          </Tag>
        ),
      },
      {
        title: t('campaigns.recipients.sent_at'),
        dataIndex: 'sent_at',
        key: 'sent_at',
        width: 160,
        render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('campaigns.recipients.opened_at'),
        dataIndex: 'opened_at',
        key: 'opened_at',
        width: 160,
        render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('campaigns.recipients.clicked_at'),
        dataIndex: 'clicked_at',
        key: 'clicked_at',
        width: 160,
        render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 180,
        render: (_: unknown, record: CampaignRecipient) => (
          <Space size="small">
            {canWrite && (
              <Select
                size="small"
                value={record.status}
                style={{ width: 110 }}
                onChange={(val) => handleUpdateRecipientStatus(record.id, val)}
                options={[
                  { value: 'pending', label: t('campaigns.recipient_status.pending') },
                  { value: 'sent', label: t('campaigns.recipient_status.sent') },
                  { value: 'delivered', label: t('campaigns.recipient_status.delivered') },
                  { value: 'opened', label: t('campaigns.recipient_status.opened') },
                  { value: 'clicked', label: t('campaigns.recipient_status.clicked') },
                  { value: 'bounced', label: t('campaigns.recipient_status.bounced') },
                  { value: 'unsubscribed', label: t('campaigns.recipient_status.unsubscribed') },
                ]}
              />
            )}
            {canWrite && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteRecipient(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, canWrite, handleUpdateRecipientStatus, handleDeleteRecipient]
  );

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (error || !campaign) {
    return (
      <div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/marketing')}>
          {t('app.back')}
        </Button>
        <Alert type="error" message={error || t('app.not_found')} style={{ marginTop: 16 }} />
      </div>
    );
  }

  const formatDate = (val: string | null | undefined) =>
    val ? new Date(val).toLocaleString() : '-';

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/marketing')}>
          {t('app.back')}
        </Button>
        {canWrite && (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/marketing/campaigns/${id}/edit`)}
          >
            {t('app.edit')}
          </Button>
        )}
        {canDelete && (
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            {t('app.delete')}
          </Button>
        )}
        {canWrite && (campaign.status === 'draft' || campaign.status === 'scheduled') && (
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            style={{ background: '#52c41a', borderColor: '#52c41a' }}
            onClick={() => handleLifecycle('launch')}
          >
            {t('campaigns.action.launch')}
          </Button>
        )}
        {canWrite && campaign.status === 'active' && (
          <Button icon={<PauseCircleOutlined />} onClick={() => handleLifecycle('pause')}>
            {t('campaigns.action.pause')}
          </Button>
        )}
        {canWrite && campaign.status === 'paused' && (
          <Button icon={<CaretRightOutlined />} onClick={() => handleLifecycle('resume')}>
            {t('campaigns.action.resume')}
          </Button>
        )}
      </Space>

      <h2>{t('campaigns.detail_title')}</h2>

      {/* Tracking Metrics */}
      {summary && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title={t('campaigns.field.total_recipients')}
                value={summary.total_recipients}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title={t('campaigns.analytics.send_rate')}
                value={summary.send_rate.toFixed(1)}
                suffix="%"
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title={t('campaigns.analytics.open_rate')}
                value={summary.open_rate.toFixed(1)}
                suffix="%"
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title={t('campaigns.analytics.click_rate')}
                value={summary.click_rate.toFixed(1)}
                suffix="%"
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title={t('campaigns.analytics.bounce_rate')}
                value={summary.bounce_rate.toFixed(1)}
                suffix="%"
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title={t('campaigns.analytics.unsubscribe_rate')}
                value={summary.unsubscribe_rate.toFixed(1)}
                suffix="%"
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Campaign Details */}
      <Descriptions bordered column={{ xs: 1, sm: 2 }}>
        <Descriptions.Item label={t('campaigns.field.name')}>{campaign.name}</Descriptions.Item>
        <Descriptions.Item label={t('campaigns.field.status')}>
          <Tag color={STATUS_COLORS[campaign.status] ?? 'default'}>
            {t(`campaigns.status_options.${campaign.status}`, campaign.status)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('campaigns.field.type')}>
          <Tag color={TYPE_COLORS[campaign.type] ?? 'default'}>
            {t(`campaigns.type_options.${campaign.type}`, campaign.type)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('campaigns.field.owner_id')}>
          {campaign.owner_id || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('campaigns.field.description')} span={2}>
          {campaign.description || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('campaigns.field.tags')}>
          {(campaign.tags?.length ?? 0) > 0
            ? campaign.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('campaigns.field.branch_id')}>
          {campaign.branch_id || '-'}
        </Descriptions.Item>
      </Descriptions>

      {/* Target Audience */}
      <h3 style={{ marginTop: 24 }}>{t('campaigns.audience.title')}</h3>
      <Descriptions bordered column={{ xs: 1, sm: 2 }}>
        <Descriptions.Item label={t('campaigns.audience.filter_industry')}>
          {campaign.target_audience?.filter_industry || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('campaigns.audience.filter_source')}>
          {campaign.target_audience?.filter_source || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('campaigns.audience.filter_status')}>
          {campaign.target_audience?.filter_status || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('campaigns.audience.filter_tags')}>
          {campaign.target_audience?.filter_tags?.join(', ') || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('campaigns.audience.estimated_count')}>
          {campaign.target_audience?.estimated_count ?? 0}
        </Descriptions.Item>
      </Descriptions>

      {/* Schedule */}
      <h3 style={{ marginTop: 24 }}>{t('campaigns.field.scheduled_at')}</h3>
      <Descriptions bordered column={{ xs: 1, sm: 3 }}>
        <Descriptions.Item label={t('campaigns.field.scheduled_at')}>
          {formatDate(campaign.schedule?.scheduled_at)}
        </Descriptions.Item>
        <Descriptions.Item label={t('campaigns.field.started_at')}>
          {formatDate(campaign.schedule?.started_at)}
        </Descriptions.Item>
        <Descriptions.Item label={t('campaigns.field.completed_at')}>
          {formatDate(campaign.schedule?.completed_at)}
        </Descriptions.Item>
      </Descriptions>

      {/* Audit */}
      <Descriptions bordered column={{ xs: 1, sm: 2 }} style={{ marginTop: 24 }}>
        <Descriptions.Item label={t('campaigns.field.created_by')}>
          {campaign.created_by || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('campaigns.field.created_at')}>
          {formatDate(campaign.created_at)}
        </Descriptions.Item>
        <Descriptions.Item label={t('campaigns.field.updated_by')}>
          {campaign.updated_by || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('campaigns.field.updated_at')}>
          {formatDate(campaign.updated_at)}
        </Descriptions.Item>
      </Descriptions>

      {/* Recipients */}
      <div style={{ marginTop: 32 }}>
        <Space style={{ marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>{t('campaigns.recipients.title')}</h3>
          <Select
            placeholder={t('campaigns.filter.status')}
            style={{ width: 140 }}
            allowClear
            value={recipientStatusFilter}
            onChange={(val) => {
              setRecipientStatusFilter(val);
              if (id) loadRecipients(id, val);
            }}
            options={[
              { value: 'pending', label: t('campaigns.recipient_status.pending') },
              { value: 'sent', label: t('campaigns.recipient_status.sent') },
              { value: 'delivered', label: t('campaigns.recipient_status.delivered') },
              { value: 'opened', label: t('campaigns.recipient_status.opened') },
              { value: 'clicked', label: t('campaigns.recipient_status.clicked') },
              { value: 'bounced', label: t('campaigns.recipient_status.bounced') },
              { value: 'unsubscribed', label: t('campaigns.recipient_status.unsubscribed') },
            ]}
          />
          {canWrite && (
            <Space.Compact>
              <Input
                placeholder={t('campaigns.recipients.select_contact')}
                value={addContactId}
                onChange={(e) => setAddContactId(e.target.value)}
                onPressEnter={handleAddRecipient}
                style={{ width: 240 }}
              />
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={handleAddRecipient}
              >
                {t('campaigns.recipients.add_recipient')}
              </Button>
            </Space.Compact>
          )}
        </Space>
        <Table<CampaignRecipient>
          columns={recipientColumns}
          dataSource={recipients}
          rowKey="id"
          loading={recipientsLoading}
          pagination={{ pageSize: 20 }}
          size="small"
          locale={{ emptyText: t('campaigns.recipients.no_recipients') }}
        />
      </div>
    </div>
  );
}
